import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { NotificationType } from '@app/shared/interfaces';
import { DirectProperty, PropertiesService } from '@app/shared/services/properties/properties.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { ClipboardService } from 'ngx-clipboard';
import { DialogCopyWidgetCodeComponent } from '../../dialogs/dialog-copy-widget-code/dialog-copy-widget-code.component';
import { DialogUpdatePropertySlugComponent } from '../../dialogs/dialog-update-property-slug/dialog-update-property-slug.component';
import { CustomSite, CustomSitesService } from '../../services/custom-sites.service';
import { DirectService, SiteCreateResponse, SitePublishStatuses } from '../../services/direct.service';

@Component({
  standalone: false,
  selector: 'sbnb-direct-properties-selection',
  templateUrl: './direct-properties-selection.component.html',
  styleUrls: ['./direct-properties-selection.component.scss'],
})
export class DirectPropertiesSelectionComponent {
  constructor(
    private toast: ToastNotificationsService,
    private dialog: MatDialog,
    public direct: DirectService,
    private _clipboardService: ClipboardService,
    private customSiteService: CustomSitesService,
    private propertiesService: PropertiesService
  ) { }
  NotificationType = NotificationType;
  SitePublishStatuses = SitePublishStatuses;

  @Input() mode: 'site' | 'widget' | 'other' = 'site';
  @Input() properties: DirectProperty[] = [];

  @Input() widget: CustomSite;
  @Input() site: SiteCreateResponse;

  @Input() futureProperties: boolean = false;
  @Input() selectedProperties: number[] = [];

  @Input() propertyUrls: { id: number; url: string | null }[] = [];

  @Input() reorderEnabled: boolean = true;

  @Input() allPropertiesEnabled: boolean = true;

  @Output() propertiesChange: EventEmitter<any[]> = new EventEmitter<null>();
  @Output() futurePropertiesChange: EventEmitter<boolean> = new EventEmitter<null>();

  ngOnInit() {
    this.sortPropertiesAccordingToSelectedProperties(this.properties, this.selectedProperties);
  }

  propertyAddedOrRemoved(event: MatCheckboxChange, propertyId: number) {
    if (event.checked) {
      this.selectedProperties = [...this.selectedProperties, propertyId];
    } else {
      const index = this.selectedProperties.findIndex((x) => x === propertyId);

      if (index > -1) {
        this.selectedProperties = [
          ...this.selectedProperties.slice(0, index),
          ...this.selectedProperties.slice(index + 1),
        ];
      }

      this.futureProperties = false;
      this.futurePropertiesChange.emit(this.futureProperties);
    }

    this.propertiesChange.emit(this.selectedProperties);

    this.sortPropertiesAccordingToSelectedProperties(this.properties, this.selectedProperties);
  }

  futurePropertiesChanged(event: MatCheckboxChange) {
    if (event.checked) {
      this.futurePropertiesChange.emit(true);

      const allowableProperties = this.properties.filter((property) => {
        // Remove the GVR check
        let checks = [...property.checks];
        checks = checks.filter((check) => check.id !== 'google_vacation_rentals');

        return !checks.some((check) => check.result === 'block');
      });

      this.propertiesChange.emit(
        allowableProperties.map((element) => {
          return element.id;
        })
      );
    } else {
      this.futurePropertiesChange.emit(false);
      this.propertiesChange.emit([]);
    }
  }

  openCopyWidgetModal(propertyId: number) {
    this.dialog.open(DialogCopyWidgetCodeComponent, {
      width: '80vw',
      maxHeight: '90vh',
      data: {
        propertyId: propertyId,
        widgetId: this.widget.booking_widget_uuid,
      },
    });
  }

  openSlugModal(property) {
    const dialogRef = this.dialog.open(DialogUpdatePropertySlugComponent, {
      width: '640px',
      maxHeight: '90vh',
      data: {
        property_id: property.id,
        site_id: this.widget?.uuid,
        mode: this.mode,
        slug: this.propertyUrls ? this.propertyUrls.find((x) => x.id === property.id)?.url : null,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && this.mode === 'site') {
        property.slug = result;
      } else if (result && this.mode === 'widget') {
        const propertyUrl = this.propertyUrls.find((x) => x.id === property.id);
        if (propertyUrl) {
          propertyUrl.url = result;
        } else {
          this.propertyUrls.push({ id: property.id, url: result });
        }
        this.propertyUrls = [...this.propertyUrls];

        this.validatePropertyUrl(property);
      }
    });
  }

  copyPropertyURL(property) {
    let url = this.direct.getSiteFullURL(this.site);

    url += '/property/' + property.slug;

    this._clipboardService.copy(url);
    this.toast.open('Copied to clipboard', 'Dismiss', NotificationType.Success, 2500);
    console.log(url);
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.properties, event.previousIndex, event.currentIndex);

    this.rebuildSelectedPropertiesAfterChangingSort();
  }

  sortPropertiesAccordingToSelectedProperties(properties: DirectProperty[], selectedProperties: number[]) {
    const sortedProperties: DirectProperty[] = [];

    for (const propertyId of selectedProperties) {
      // Find the property object in the properties array that matches the current property ID
      const property = properties.find((p) => p.id === propertyId);

      // If the property object is found, add it to the sortedProperties array
      if (property) {
        sortedProperties.push(property);
      }
    }

    // Add any remaining properties that are not in the selectedProperties array to the end of the sortedProperties array
    for (const property of properties) {
      if (!selectedProperties.includes(property.id)) {
        sortedProperties.push(property);
      }
    }

    // Update the properties array to match the sortedProperties array
    this.properties = sortedProperties;
  }

  rebuildSelectedPropertiesAfterChangingSort() {
    const newSelectedProperties: number[] = [];

    // this.properties should be in the desired order now
    for (const property of this.properties) {
      // Check if the property ID exists in the selectedProperties array
      const index = this.selectedProperties.findIndex((x) => x === property.id);

      // If the property ID is found in the selectedProperties array, add it to the newSelectedProperties array
      if (index > -1) {
        newSelectedProperties.push(property.id);
      }
    }

    // Update the selectedProperties array to match the newSelectedProperties array
    this.selectedProperties = newSelectedProperties;

    // Emit the updated selectedProperties array
    this.propertiesChange.emit(this.selectedProperties);

    this.sortPropertiesAccordingToSelectedProperties(this.properties, this.selectedProperties);
  }

  validatePropertyUrl(property: DirectProperty) {
    const foundProperty = this.properties.find((x) => x.id === property.id);

    if (!foundProperty.checks) {
      return;
    }

    let customSiteCheck = foundProperty.checks.find((x) => x.id === 'custom_site');

    if (!customSiteCheck) {
      return;
    }

    customSiteCheck.result = 'loading';

    this.customSiteService.validatePropertyUrl(this.widget.uuid, property.id).subscribe((res) => {
      this.propertiesService.getDirectProperties(null, this.widget.uuid, property.id).subscribe((res) => {
        if (res[0]) {
          property.checks = res[0].checks;
        }
      });
    });
  }
}
